import React, {  useEffect, useMemo, useRef, useState } from "react";
import { GoSearch } from "react-icons/go";
import { FiChevronRight, FiInfo } from "react-icons/fi";
import { TfiClose } from "react-icons/tfi";

import ContainerResultSearch from "../../SearchPage/components/ResultMultiSearchPopup";
import {CurrentCity,getMultiSearchResults, normalizeCitySearch, topCities, VilleSuggestion} from "../../SearchPage/hookSearchPage";
import axios from "axios";
import {  slugify } from "../../../../components/types/types";
import { IoMdPin } from "react-icons/io";
import { useViewportMaxHeight } from "../../../../components/hook/hookViewPortHeight";

interface Props {
    showPopupMultiSearch: boolean;
    setShowPopupMultiSearch: React.Dispatch<React.SetStateAction<boolean>>;

    suggestions: any[];
    miniRestos: any[];


    multiSearchPopupRef: React.RefObject<HTMLDivElement>;
    multiSearchMaxHeight: number | null;

    searchPartRef: React.RefObject<HTMLDivElement>;

    handleSearch: () => void;
}

export const MultiSearchDesktop: React.FC<Props> = React.memo(({showPopupMultiSearch,setShowPopupMultiSearch,suggestions,miniRestos,multiSearchPopupRef,multiSearchMaxHeight,searchPartRef,handleSearch}) => {

    const [multiSearchValue, setMultiSearchValue] = useState("");
    const [debouncedValue, setDebouncedValue] = useState("");

    const multiSearchInputRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
        const isCoarse = window.matchMedia("(pointer: coarse)").matches;
        if (!isCoarse) return;
        const handlePointerDown = (e: PointerEvent) => {
            const input = multiSearchInputRef.current;
            if (!input) return;
            if (document.activeElement !== input) return;
            if (e.target instanceof Node && input.contains(e.target)) return;
            input.blur();
        };
        document.addEventListener("pointerdown", handlePointerDown, {
            passive: true,
        });

        return () => {
            document.removeEventListener("pointerdown", handlePointerDown);
        };
    }, []);
    

    const handleClickSearch = () => {
        setMultiSearchValue("")
        setDebouncedValue("")
        handleSearch()
    }

    useEffect(() => {
        const timeout = window.setTimeout(() => {
            setDebouncedValue(multiSearchValue);
        }, 120);

        return () => {
            window.clearTimeout(timeout);
        };
    }, [multiSearchValue]);

    const multiSearchResults = useMemo(() => {
        if (!debouncedValue) return [];

        return getMultiSearchResults({
            search: debouncedValue,
            miniRestos,
        });
    }, [debouncedValue,miniRestos]);

    const blurMultiSearchOnPopupTouch = (e: React.TouchEvent<HTMLDivElement>) => {
        const input = multiSearchInputRef.current;
        if (!input) return;
        if (document.activeElement !== input) return;
        if (e.target instanceof Node &&input.contains(e.target)) return;
        input.blur();
    };

    return (
        <div className={`multiSearchPart ${showPopupMultiSearch ? 'isSelect' : ''}`}>
            <div ref={searchPartRef} className="containerIconMultiSearch" onClick={handleClickSearch} style={{display:'flex'}}><GoSearch className="iconMultiSearch pointer lowOpacityHover" size={23} onClick={() => setShowPopupMultiSearch(!showPopupMultiSearch)} /></div>
            {
            showPopupMultiSearch && <div className='containerPopup primaryBack smallText' ref={multiSearchPopupRef}  onTouchStartCapture={blurMultiSearchOnPopupTouch} style={multiSearchMaxHeight ? { maxHeight: `${multiSearchMaxHeight}px` } : undefined}>
                <div className='containerInput '>
                    <GoSearch className="icon" size={18} />
                    <input ref={multiSearchInputRef} type="text" className="XsmallText" placeholder="Rechercher un restaurant..." value={multiSearchValue} onChange={((e) => {setMultiSearchValue(e.target.value);setShowPopupMultiSearch(true)})} onFocus={() => setShowPopupMultiSearch(true)} />
                    {multiSearchValue && <TfiClose className='iconClose pointer' onClick={(e) => {e.preventDefault();e.stopPropagation();setMultiSearchValue("")}}/>}
                </div>
                <div className="containerAllSuggest">
                    <div className="shadowTop"></div>
                    <div className="shadowBottom"></div>
                    {multiSearchValue.length > 0 ? <ContainerResultSearch multiSearchResults={multiSearchResults} />
                    : suggestions && suggestions.length > 0 ? 
                        <ContainerResultSearch multiSearchResults={suggestions} /> : <></>
                        }
                 </div>
                </div>
            }
        </div>
    );
});
const PROXIMITY_CITY: NonNullable<CurrentCity> = {
    name: "À proximité",
    slug: "restaurants-a-proximite",
};
interface CitySearchDesktopProps {
    showPopupCity: boolean;
 
    currentCity: CurrentCity | null;
    setCurrentCity: React.Dispatch<React.SetStateAction<CurrentCity | null>>;

    handleClickCity: (city: CurrentCity) => void;
    handleCity: () => void;

    cityPartRef: React.RefObject<HTMLDivElement>;
    miniRestos: any[];

    impossibleCity: boolean;
    setImpossibleCity: React.Dispatch<React.SetStateAction<boolean>>;
}

export const CitySearchDesktop: React.FC<CitySearchDesktopProps> = React.memo(({showPopupCity,currentCity,setCurrentCity,handleClickCity,handleCity,cityPartRef,miniRestos,impossibleCity,setImpossibleCity}) => {

    const [currentSearchCity, setCurrentSearchCity] = useState("");
    const [suggestionsCity, setSuggestionsCity] = useState<VilleSuggestion[]>([]);
    const [isCitySearchActive, setIsCitySearchActive] = useState(false);

    const citySearchInputRef = useRef<HTMLInputElement>(null);


    useEffect(() => {
        const isCoarse = window.matchMedia("(pointer: coarse)").matches;
        if (!isCoarse) return;

        const handlePointerDown = (e: PointerEvent) => {
            const input = citySearchInputRef.current;
            if (!input) return;
            if (document.activeElement !== input) return;

            if (e.target instanceof Node && input.contains(e.target)) return;

            input.blur();
        };

        document.addEventListener("pointerdown", handlePointerDown, {
            passive: true,
        });

        return () => {
            document.removeEventListener("pointerdown", handlePointerDown);
        };
    }, []);

    const showTopCities = currentSearchCity.trim().length < 1 || !isCitySearchActive;
    useEffect(() => {
        if (showPopupCity) return;

        setCurrentSearchCity("");
        setSuggestionsCity([]);
        setIsCitySearchActive(false);
    }, [showPopupCity]);

    const cityListToDisplay = useMemo(() => {
        if(!showTopCities ) return suggestionsCity.slice(0, 5).map(city => ({ name: city.name, slug: slugify(city.name),latitude: String(city.latitude),longitude: String(city.longitude),cityNameRaw: city.name, }))
        if (!currentCity) return topCities;
        const normalizedCurrentCity = normalizeCitySearch(currentCity.name);
        return topCities.filter( city => normalizeCitySearch(city.name) !== normalizedCurrentCity);
    }, [showTopCities, suggestionsCity,currentCity]);
  const cityCountMap = useMemo(() => {
        const map = new Map();
        for (const resto of miniRestos) {
            const keys = new Set([resto._addressCityNorm, resto._visibleCityNorm]);
            keys.forEach((key) => {
                if (!key) return;
                map.set(key, (map.get(key) || 0) + 1);
            });
        }
        return map;
    }, [miniRestos]);
    const cityListWithCount = useMemo(() => {
        const cities = cityListToDisplay.map((city) => ({
            ...city,
            restaurantCount:
                city.name === "À proximité"
                    ? null
                    : cityCountMap.get(normalizeCitySearch(city.name)) ?? 0
        }));
        return cities.sort((a, b) => (b.restaurantCount ?? 0) - (a.restaurantCount ?? 0));
    }, [cityListToDisplay, cityCountMap, showTopCities]);
    

    useEffect(() => {
      const search = currentSearchCity.trim();

      if (!showPopupCity) return;

      if (search.length < 1) {
          setSuggestionsCity([]);
          return;
      }
      const controller = new AbortController();
        const timeout = window.setTimeout(async () => {
            try {
                const response = await axios.post(
                    `${process.env.REACT_APP_DIRECTION_REQUEST}/api/getVille`,
                    { name: search },
                    { signal: controller.signal }
                );

                setSuggestionsCity(response.data || []);
            } catch (error) {
                if (axios.isCancel(error)) return;

                console.error(
                    "Erreur lors de la recherche des villes:",
                    error
                );

                setSuggestionsCity([]);
            }
        }, 250);

        return () => {
            window.clearTimeout(timeout);
            controller.abort();
        };
  }, [currentSearchCity,showPopupCity]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCurrentSearchCity(e.target.value);
        setIsCitySearchActive(true);
    };

    const handleSelectCity = (city: CurrentCity & {restaurantCount?: number | null}) => {
        if (city.restaurantCount !== null && city.restaurantCount === 0) {
            setImpossibleCity(true);
            return;
        }
        handleClickCity(city);
    };
    const {ref: cityPopupRef,maxHeight: cityMaxHeight} = useViewportMaxHeight(showPopupCity);

    const blurCityOnPopupTouch = (e: React.TouchEvent<HTMLDivElement>) => {
        const input = citySearchInputRef.current;
        if (!input) return;
        if (document.activeElement !== input) return;
        if (e.target instanceof Node &&input.contains(e.target)) return;
        input.blur();
    };

    return (
        <div className={`cityPart ${showPopupCity ? 'isSelect' : ''}`} ref={cityPartRef}>
            {impossibleCity && showPopupCity && <div className="containerCantCity smallText">
                <FiInfo className='icon' />
                <span>QuelResto n'est pas encore présent dans cette ville.</span>
            </div>}
            <div className="basePart pointer lowOpacityHover" onClick={handleCity}>
                
                <IoMdPin size={16} className="icon"/>
                <span className={`textBase smallText`}>
                    {currentCity ? (currentCity.name === 'Restaurants-A-Proximite' ? 'À proximité' : currentCity.name) : 'Où voulez-vous manger ?'}
                </span>

                {currentCity && <TfiClose className='iconClose pointer' onClick={(e) => {e.preventDefault();e.stopPropagation();setCurrentCity(null)}}/>}
            </div>
            {showPopupCity && <div className='containerPopup primaryBack' ref={cityPopupRef}  onTouchStartCapture={blurCityOnPopupTouch} style={cityMaxHeight ? { maxHeight: `${cityMaxHeight}px` } : undefined}>
                    <div className="containerInput">
                        <GoSearch className="iconSearch" size={16}/>
                        <input
                            ref={citySearchInputRef}
                            className='smallText'
                            placeholder="Rechercher une ville..."
                            value={currentSearchCity}
                            onChange={handleInputChange} />
                         {currentSearchCity && <TfiClose className='iconClose pointer' onClick={(e) => {e.preventDefault();e.stopPropagation();setCurrentSearchCity("")}}/>}
                        <div className="shadowTop"></div>
                    </div>
                    <div className={`suggestList XsmallText ${!showTopCities ? 'alignTop' : ''}`}>
                        <div className="shadowBottom"></div>
                        {(showTopCities && currentCity?.slug !== "restaurants-a-proximite") && (
                            <>
                                <div className="elemSuggestion pointer lowOpacityHover" onClick={() => handleSelectCity(PROXIMITY_CITY)}>
                                    <div className='containerIconSearch flexAllCenter'><GoSearch className="iconSearch" size={13}/></div>
                                    <span>À proximité</span>
                                    <div className="containerIcon flexAllCenter"><FiChevronRight size={13} className="icon" /></div>
                                </div>
                                <div className="separationList"></div>
                            </>
                        )}
                        {cityListWithCount && cityListWithCount.length > 0 ? cityListWithCount.map((elem,id) => {
                            return <React.Fragment key={'suggestCity'+elem.slug}>
                                <div className={`elemSuggestion pointer lowOpacityHover ${elem.restaurantCount === 0 ? 'isLock' : ''}`}  onClick={() => handleSelectCity(elem)}>
                                    <div className='containerIconSearch flexAllCenter'><GoSearch className="iconSearch" size={13}/></div>
                                    <span>
                                        {elem.name}
                                        {elem.restaurantCount !== null && (
                                            <span className="citySuggestionCount">
                                                {`(${elem.restaurantCount})`}
                                            </span>
                                        )}</span>
                                    <div className="containerIcon flexAllCenter"><FiChevronRight size={13} className="icon"/></div>
                                </div>
                                {id < cityListWithCount.length-1 && <div className="separationList"></div>}
                            </React.Fragment>
                        })
                        : <span className="XsmallText textNoSuggest" >Aucune ville correspondante dans le Maine-et-Loire.</span>}
                    </div>
                
            </div>}
        </div>
    )
})